import { hasRole } from '../../../guards/auth/roles';
import {
  Component,
  ViewEncapsulation,
  Input,
  OnInit,
  EventEmitter,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { CalendarConfigModule } from '../../../calendar-config';
import {
  CalendarEvent,
  CalendarMonthViewBeforeRenderEvent,
  CalendarView,
  CalendarMonthViewDay,
} from 'angular-calendar';

import {
  startOfDay,
  isSameDay,
  startOfMonth,
  format,
  endOfMonth,
  endOfWeek,
  startOfWeek,
  sub,
} from 'date-fns';
import { CalendarHelpersComponent } from '../../atoms/calendar-helpers/calendar-helpers.component';
import { FilterComponent } from '../filter/filter.component';
import {
  Filter,
  Sendout,
  Event,
  SendoutStatus,
} from '../../../types';
import { Router } from '@angular/router';
import { DialogService } from '../../../services/dialog/dialog.service';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { isMonday, getISOWeek } from 'date-fns';
import {
  CalendarDate,
  CalendarDateAvailabilityReason,
  CalendarService,
} from '../../../services/calendar/calendar.service';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import {
  GlobalDataService,
  SessionData,
} from '../../../services/global-data/global-data.service';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-full-calendar',
  standalone: true,
  imports: [
    CommonModule,
    CalendarConfigModule,
    CalendarHelpersComponent,
    FilterComponent,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './full-calendar.component.html',
  styleUrls: ['./full-calendar.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class FullCalendarComponent implements OnInit {
  @Input() storeId: number = 0;
  @Input() sendouts: Sendout[] = [];
  @Input() events: Event[] = [];
  @Input() headerTitle: string = 'Kalenderoverblik';
  @Input() showHeader = true;
  @Input() showLegend = true;
  @Input() mode: 'create-sendout' | 'select-date' = 'create-sendout';
  @Input() currentSendoutId?: number;

  @Output() dateSelected = new EventEmitter<Date>();

  locale: string = 'da';

  templateFilters: Filter[] = [
    {
      id: 1,
      text: 'Udsendelse',
      classes: 'custom-underline udsendelse-bg',
      checked: true,
    },
    {
      id: 2,
      text: 'Event',
      classes: 'custom-underline event-bg',
      checked: true,
    },
    {
      id: 3,
      text: 'Kommentar',
      classes: 'custom-underline comment-bg',
      checked: true,
    },
  ];

  today = startOfDay(new Date());
  view: CalendarView = CalendarView.Month;
  startOfMonthDate: Date = startOfMonth(this.today);
  @Input() viewDate: Date = startOfMonth(this.today);

  displayPrevButton = true;
  displayNextButton = true;

  calendarData: CalendarDate[] = [];

  calenderEvents$: BehaviorSubject<CalendarEvent[]> = new BehaviorSubject<
    CalendarEvent[]
  >([]);

  isLoading: boolean = false;

  SendoutStatus = SendoutStatus;

  session: SessionData | null = null;

  constructor(
    private router: Router,
    private dialogService: DialogService,
    private snackbarService: SnackbarService,
    private calendarService: CalendarService,
    private globalDataService: GlobalDataService
  ) {}

  async ngOnInit(): Promise<void> {
    this.session = await firstValueFrom(
      this.globalDataService.getSessionData()
    );
    if (!this.isLoading) {
      await this.loadCalendarData();
    }
  }

  async viewDateChanged(date: Date) {
    if (!this.isLoading) {
      await this.loadCalendarData();
    }
  }

  async getStoreId(): Promise<number> {
    if (!this.storeId) {
      this.storeId = this.session?.store?.id || 0;
    }
    return this.storeId;
  }

  async loadCalendarData(): Promise<void> {
    this.isLoading = true;
    const storeId = await this.getStoreId();

    let from = sub(
      startOfWeek(startOfMonth(this.viewDate), { weekStartsOn: 1 }),
      {
        days: 7,
      }
    );
    let to = endOfWeek(endOfMonth(this.viewDate), { weekStartsOn: 1 });

    this.calendarService
      .getCalendar(this.storeId, from, to, this.currentSendoutId)
      .subscribe((data) => {
        this.calendarData = data;
        let calendarEvents: CalendarEvent[] = [];
        this.calendarData.forEach((calendarDate) => {
          calendarDate.sendouts.forEach((sendout) => {
            calendarEvents.push({
              id: sendout.id,
              start: new Date(calendarDate.date),
              end: new Date(calendarDate.date),
              title: sendout.sendoutName,
              cssClass: 'sendout-item',
              meta: {
                type: 'sendout',
                sendoutId: sendout.id,
                status: sendout.status,
              },
            });
          });
          calendarDate.events.forEach((event) => {
            calendarEvents.push({
              id: event.id,
              start: new Date(calendarDate.date),
              end: new Date(calendarDate.date),
              title: event.eventName,
              cssClass: 'event-item',
              meta: {
                type: 'event',
                eventUrl: event.eventUrl,
              },
            });
          });
          calendarDate.calendarItems.forEach((item) => {
            if (item.type == 'Comment')
              calendarEvents.push({
                id: item.id,
                start: new Date(calendarDate.date),
                end: new Date(calendarDate.date),
                title: item.description || '',
                cssClass: `calendar-item-${item.type}`,
                meta: {
                  type: item.type,
                  item: item,
                },
              });
          });
        });
        this.calenderEvents$.next(calendarEvents);
        this.isLoading = false;
      });
  }

  // Exporting calendar functions to be used in the template
  isMonday(date: Date): boolean {
    return isMonday(date);
  }

  getISOWeek(date: Date): number {
    return getISOWeek(date);
  }

  navigateToSendoutWithDate = (date: Date) => {
    this.router.navigate(['sendout', 'create'], {
      queryParams: { sendoutDate: format(date, 'yyyy-MM-dd') },
    });
  };

  dayClicked({ date, events }: { date: Date; events: CalendarEvent[] }): void {
    const calendarDateEntry = this.calendarData.find((e) =>
      isSameDay(e.date, date)
    );
    if (!calendarDateEntry) {
      console.error('Calendar date entry not found');
      return;
    }

    if (
      !calendarDateEntry.allow &&
      calendarDateEntry.reason == CalendarDateAvailabilityReason.IsInThePast
    ) {
      this.snackbarService.show('Der kan ikke oprettes udsendelser i fortiden');
      return;
    }

    if (
      !calendarDateEntry.allow &&
      !(this.session && hasRole(this.session, 'SendoutManager'))
    ) {
      let message;
      switch (calendarDateEntry.reason) {
        case CalendarDateAvailabilityReason.HasSendoutSameDay:
          message = 'Der er allerede oprettet en udsendelse på denne dato';
          break;

        case CalendarDateAvailabilityReason.IsInThePast:
          message = 'Der kan ikke oprettes udsendelser i fortiden';
          break;
        case CalendarDateAvailabilityReason.IsToday:
        case CalendarDateAvailabilityReason.IsInTheNextTwoDays:
        case CalendarDateAvailabilityReason.InsufficientApprovalTime:
          message =
            'Der kan ikke oprettes udsendelser, hvis administrator har mindre end to arbejdsdage til at godkende';
          break;
        case CalendarDateAvailabilityReason.IsSunday:
          message = 'Der kan ikke oprettes udsendelser på søndage';
          break;
        case CalendarDateAvailabilityReason.DenyOverride:
          message = 'Der kan ikke oprettes udsendelser på denne dato';
          break;
        default:
          message = `'${calendarDateEntry.reason}'`;
          break;
      }
      this.snackbarService.show(message);
    } else {
      if (
        calendarDateEntry.warn &&
        !(this.session && hasRole(this.session, 'SendoutManager'))
      ) {
        this.dialogService.openDialog(
          'Er du sikker på, at du vil oprette en udsendelse?',
          'Du er ved at oprette en udsendelse, som er mindre end syv dage før eller efter en anden udsendelse.',
          'Opret udsendelse',
          () => {
            if (this.mode == 'create-sendout') {
              this.navigateToSendoutWithDate(date);
            } else if (this.mode == 'select-date') {
              this.dateSelected.emit(date);
            }
          },
          undefined,
          '450px',
          'content-container',
          '',
          '',
          'dialog-actions'
        );
      } else {
        if (this.mode == 'create-sendout') {
          this.navigateToSendoutWithDate(date);
        } else if (this.mode == 'select-date') {
          this.dateSelected.emit(date);
        }
      }
    }
  }

  eventClicked(event: CalendarEvent): void {
    if (event.meta.type === 'event') {
      const eventLink = event.meta.eventUrl;
      window.open(eventLink, '_blank');
    } else if (event.meta.type == 'sendout') {
      const sendoutId = event.id as number;
      this.router.navigate(['sendout', sendoutId]);
    }
  }

  async onFilterChange(): Promise<void> {
    const showSendouts = this.templateFilters.find(
      (f) => f.text === 'Udsendelse'
    )?.checked;
    const showEvents = this.templateFilters.find(
      (f) => f.text === 'Event'
    )?.checked;

    const calenderEvents: CalendarEvent<any>[] = [];

    if (showSendouts) {
      this.sendouts.forEach((sendout) => {
        if (sendout.sendoutDate !== null) {
          calenderEvents.push({
            id: sendout.id,
            start: new Date(sendout.sendoutDate),
            end: new Date(sendout.sendoutDate),
            title: sendout.sendoutName,
          });
        }
      });
    }

    if (showEvents) {
      this.events.forEach((event) => {
        calenderEvents.push({
          id: event.id,
          start: new Date(event.startsAt),
          end: new Date(event.endsAt),
          title: event.name,
          meta: { eventUrl: event.link },
        });
      });
    }
    this.calenderEvents$.next(calenderEvents);
  }

  isLockPeriod(date: Date): boolean {
    let calendarDateEntry = this.calendarData.find((e) =>
      isSameDay(e.date, date)
    );
    if (calendarDateEntry) {
      return !calendarDateEntry.allow;
    }
    return false;
  }

  isLockPeriodStyled(date: Date): boolean {
    const showLockPeriods = this.templateFilters.find(
      (f) => f.text === 'Låst periode'
    )?.checked;
    if (!showLockPeriods) {
      return false;
    }
    return this.isLockPeriod(date);
  }

  dayHasExplicitDeny(day: CalendarMonthViewDay): {
    hasDeny: boolean;
    label: string;
  } {
    const event = day.events.find((e) => e.meta.type === 'DenyDate');
    if (event) {
      return { hasDeny: true, label: event.title };
    }
    return { hasDeny: false, label: '' };
  }

  onBeforeViewRender(event: CalendarMonthViewBeforeRenderEvent) {
    event.body.forEach((day: CalendarMonthViewDay) => {
      day.meta = {
        allow: true,
        showLockIcon: false,
        reason: '',
      };
      const calendarDataEntry = this.calendarData.find((e) =>
        isSameDay(e.date, day.date)
      );

      if (calendarDataEntry) {
        day.meta.allow = calendarDataEntry.allow;
        const deny = calendarDataEntry.calendarItems.find(
          (i) => i.type === 'DenyDate'
        );
        if (deny) {
          day.meta.showLockIcon = true;
          day.meta.reason = deny.description;
        }
      }
    });
  }
}
